import React from "react";
import { Box, Slider, SliderProps, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { BUTTON_HEIGHT } from "./buttonHeight";

type LabeledSliderProps = SliderProps & {
  label: string;
  // The control's overall width; the slider fills what the label leaves.
  width?: number | string;
};

// A slider dressed as a button: the same outlined blue-on-white border, the same corner radius and
// exactly the same height, with its label in small caps above the track. Sliders otherwise float
// unlabelled in a toolbar and sit at a different height from everything beside them.
//
// Every Slider prop passes through (min, max, step, onChangeCommitted, valueLabelDisplay, …), so
// this only supplies the dress.
const LabeledSlider = ({ label, width = 180, size = "small", sx, ...props }: LabeledSliderProps) => (
  <Box
    sx={{
      width,
      height: BUTTON_HEIGHT[size],
      px: 1.25,
      pt: 0.25,
      display: "flex",
      flexDirection: "column",
      // Packed to the top rather than centred, so the label sits near the border and the track
      // takes the room left over.
      justifyContent: "flex-start",
      border: 1,
      borderRadius: 1,
      borderColor: (theme) => alpha(theme.palette.primary.main, 0.5),
      color: "primary.main",
      "&:hover": { borderColor: "primary.main" },
    }}
  >
    <Typography
      component="div"
      sx={{
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        fontWeight: 500,
        fontSize: "0.5625rem",
        lineHeight: 1,
        textAlign: "center",
        color: "primary.main",
      }}
    >
      {label}
    </Typography>
    {/* The track is pulled tight so the label and it together fit a button's height, and dropped a
        little clear of the label rather than sitting straight under it. */}
    <Slider
      size={size}
      sx={{
        mt: 1,
        py: 0,
        display: "block",
        "& .MuiSlider-thumb": { width: 10, height: 10 },
        ...sx,
      }}
      {...props}
    />
  </Box>
);

export default LabeledSlider;
